import {
  getActionCardKind,
  isSlackMessage,
  isTodoActionCard,
  type ActionCardProvider,
  type DecodedActionCard,
} from '@littlebird/action-cards';

import { Icons, type LBIcon } from '@/assets/icons/icons';
import { FeedTile } from '@/features/action-cards-view/feed';
import {
  FeedCheckbox,
  type FeedCheckboxState,
} from '@/features/action-cards-view/feed/FeedCheckbox';
import { meetingText } from '@/features/action-cards-view/feed/FeedSourceChip';
import { providerIcon as integrationProviderIcon } from '@/features/action-cards-view/registry/internals/providerIdentity';
import { ActionCardIcon } from '@/features/action-cards/components/ActionCardIcon';
import { providerIcon } from '@/features/action-cards/icons';
import { isWorkingEntry } from '@/features/home/need-you-cards';
import type {
  PendingActionCardEntry,
  PendingActionCardProvenance,
} from '@/features/home/use-waiting-for-you';
import { cn } from '@/lib/utils';

import type { ActionCard } from '@littlebird/sdk';

// A DM's id is a user (U, W) or a DM channel (D), as the backend's is_direct_message reads it
const inSlackChannel = (content: DecodedActionCard['content']) =>
  (isSlackMessage(content) || content.type === 'slack_reaction') &&
  !/^[UWD]/.test(content.channel_id);

const SOURCE_APP_LABEL = {
  gmail: 'Gmail',
  outlook: 'Outlook',
  'google-calendar': 'Google Calendar',
  todoist: 'Todoist',
  slack: 'Slack',
  github: 'GitHub',
  linear: 'Linear',
} as const;

type SourceApp = keyof typeof SOURCE_APP_LABEL;

const APP_OF_HOST: readonly [RegExp, SourceApp][] = [
  [/^mail\.google\.com$/, 'gmail'],
  [/^calendar\.google\.com$/, 'google-calendar'],
  [/^outlook\.(live|office|office365)\.com$/, 'outlook'],
  [/(^|\.)slack\.com$/, 'slack'],
  [/(^|\.)github\.com$/, 'github'],
  [/(^|\.)linear\.app$/, 'linear'],
  [/(^|\.)todoist\.com$/, 'todoist'],
];

const appOfUrl = (url: string | null | undefined): SourceApp | undefined => {
  if (!url) return undefined;
  try {
    const host = new URL(url).hostname;
    return APP_OF_HOST.find(([pattern]) => pattern.test(host))?.[1];
  } catch {
    return undefined;
  }
};

// The app a to-do or an update came from. Its link says where it really lives, so a known link wins over the app the
// agent named: a GitHub email read in Gmail links to Gmail
export const sourceAppOf = (source: {
  app: SourceApp;
  url?: string | null;
}): SourceApp => appOfUrl(source.url) ?? source.app;

// The app a card is about. A Littlebird card made from an integration's finding shows the app it came from
export const feedCardProvider = ({
  decoded,
}: {
  decoded: DecodedActionCard;
}): ActionCardProvider => {
  const { content, provider } = decoded;
  if (content.type === 'tool_approval' && content.app_name === 'Devin')
    return { ...provider, label: 'Devin' };
  const app =
    content.type === 'informational' && content.source
      ? sourceAppOf(content.source)
      : null;
  return app ? { id: app, label: SOURCE_APP_LABEL[app] } : provider;
};

const brandGlyph =
  (Glyph: LBIcon) =>
  ({ className }: { className?: string }) => (
    <Glyph
      aria-hidden="true"
      className={cn(
        className,
        'text-brand-cyan-cornflower-blue dark:text-brand-jordy-blue'
      )}
    />
  );

const UpdateGlyph = brandGlyph(Icons.bell);
const TodoGlyph = brandGlyph(Icons.square);
const DoneTodoGlyph = brandGlyph(Icons.squareCheck);
const MeetingGlyph = brandGlyph(Icons.meetings);

// A type this app does not know, such as a plugin's, shows the Littlebird mark, not the bell of an informational update
const unknownTypeTile = (actionType: string) =>
  getActionCardKind(actionType) ? null : (
    <FeedTile glyph={providerIcon.Littlebird} label="Littlebird" littlebird />
  );

const InProgressTodoGlyph = ({ className }: { className?: string }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className={cn(className, 'text-kind-done')}
    fill="currentColor"
  >
    <circle cx="12" cy="12" r="8" />
  </svg>
);

export const feedCardIcon = ({
  card,
  decoded,
}: {
  card: Pick<ActionCard, 'actionType' | 'row'>;
  decoded: DecodedActionCard;
}) => {
  const unknownType = unknownTypeTile(card.actionType);
  if (unknownType) return unknownType;
  const { content } = decoded;
  const provider = feedCardProvider({ decoded });
  if (content.type === 'tool_approval' && content.app_name === 'Devin')
    return <FeedTile glyph={integrationProviderIcon('devin')} label="Devin" />;
  // Linear cards always show the Linear tile, even when the issue names a person
  const toPerson =
    content.type === 'teammate_message' ||
    ('person' in content &&
      Boolean(content.person) &&
      provider.id !== 'linear' &&
      !inSlackChannel(content));
  if (toPerson)
    return (
      <span className="feed-avatar">
        <ActionCardIcon content={content} provider={provider} />
      </span>
    );
  if (content.type === 'informational') {
    const todo = isTodoActionCard(content);
    const done =
      decoded.status === 'completed' ||
      decoded.status === 'completed_externally';
    const inProgress =
      todo &&
      decoded.status === 'draft' &&
      card.row.facts?.chat_started === true &&
      card.row.facts.chat_auto_started !== true;
    const todoGlyph = done
      ? DoneTodoGlyph
      : inProgress
        ? InProgressTodoGlyph
        : TodoGlyph;
    const kindGlyph = todo ? todoGlyph : UpdateGlyph;
    const statusLabel = inProgress ? 'In progress' : undefined;

    if (content.source)
      return (
        <FeedTile
          glyph={providerIcon[sourceAppOf(content.source)]}
          label={provider.label}
          badge={kindGlyph}
          statusLabel={statusLabel}
        />
      );
    return (
      <FeedTile
        glyph={kindGlyph}
        label={todo ? 'To-do' : 'Update'}
        statusLabel={statusLabel}
        tint={todo ? 'bg-brand-jordy-blueA/20' : 'bg-brand-jordy-blueA/15'}
      />
    );
  }

  const littlebird = provider.id === 'Littlebird';
  return (
    <FeedTile
      glyph={littlebird ? providerIcon.Littlebird : providerIcon[provider.id]}
      label={littlebird ? 'Littlebird' : provider.label}
      littlebird={littlebird}
    />
  );
};

const checkboxGlyph =
  (state: FeedCheckboxState) =>
  ({ className }: { className?: string }) => (
    <FeedCheckbox state={state} className={className} />
  );

const IDLE_BOX = checkboxGlyph('idle');
const WORKING_BOX = checkboxGlyph('working');
const DONE_BOX = checkboxGlyph('done');

// The Feed's row icon. A to-do or a draft is a box, grey at rest and blue while the user works on it, with the app it
// is about as a badge at its foot; an update is its app's plain mark. A meeting's card badges its box with the meeting
// in place of the app, since the open card already links the app and nothing else on the row names the meeting
export const feedCheckIcon = (
  entry: PendingActionCardEntry,
  provenance?: PendingActionCardProvenance
) => {
  const unknownType = unknownTypeTile(entry.card.actionType);
  if (unknownType) return unknownType;
  const { decoded } = entry;
  const { content } = decoded;
  const provider = feedCardProvider({ decoded });
  // An update is read, not done, so it keeps the plain mark of the app it came from
  if (content.type === 'informational' && !isTodoActionCard(content))
    return content.source ? (
      <FeedTile
        glyph={providerIcon[sourceAppOf(content.source)]}
        label={provider.label}
      />
    ) : (
      <FeedTile
        glyph={UpdateGlyph}
        label="Update"
        tint="bg-brand-jordy-blueA/15"
      />
    );
  const done =
    decoded.status === 'completed' || decoded.status === 'completed_externally';
  const working = !done && isWorkingEntry(entry);
  const appGlyph =
    content.type === 'tool_approval' && content.app_name === 'Devin'
      ? integrationProviderIcon('devin')
      : content.type === 'informational'
        ? content.source
          ? providerIcon[sourceAppOf(content.source)]
          : undefined
        : provider.id === 'Littlebird'
          ? undefined
          : providerIcon[provider.id];

  const meetingLabel = provenance?.meeting
    ? `From the ${meetingText(provenance.meeting.name)} · ${provenance.ageLabel}`
    : undefined;

  return (
    <span className="feed-check-tile">
      <FeedTile
        glyph={done ? DONE_BOX : working ? WORKING_BOX : IDLE_BOX}
        label={provider.label}
        badge={meetingLabel ? MeetingGlyph : appGlyph}
        badgeLabel={meetingLabel}
        statusLabel={working ? 'In progress' : undefined}
        tint="bg-transparent"
      />
    </span>
  );
};
